/**
 * Staff calls as the platform sends them (routers/calls.py), and their
 * translation into the web app's names.
 *
 * Read leniently on purpose: a field the platform leaves out is a default
 * here rather than a refused poll, because a poll refused for a missing
 * `last_seen_at` is a call that never rings. The fields that decide whether a
 * call connects — ids, states, the signal's kind and payload — are required.
 */
import { z } from "zod";
import {
  defaultPolicy,
  LEVELS,
  type Call,
  type CallDirectory,
  type CallPerson,
  type CallMessage,
  type CallPoll,
  type CallPolicy,
  type DndState,
  type IceConfig,
  type JoinRule,
  type Level,
  type LiveCall,
  type Meeting,
  type Participant,
  type PolicyEnd,
  type PolicyView,
  type Signal,
} from "../domain/calls";

const str = z.string().nullish();
const bool = (d: boolean) =>
  z
    .boolean()
    .nullish()
    .transform((v) => v ?? d);

export const level = (v: string | null | undefined): Level =>
  (LEVELS as readonly string[]).includes(v ?? "") ? (v as Level) : "operations";

const wirePhone = z.object({ label: str, value: z.string(), kind: str });

const wirePerson = z.object({
  principal_id: z.string(),
  name: z.string(),
  level: str,
  roles: z.array(z.string()).nullish(),
  job_title: str,
  department: str,
  employee_id: str,
  has_photo: bool(false),
  online: bool(false),
  device: str,
  last_seen_at: str,
  can_call: bool(true),
  can_video: bool(true),
  reason: str,
  phones: z.array(wirePhone).nullish(),
  /* v2 */
  dnd: bool(false),
  dnd_until: str,
});

export const wireDirectory = z.object({
  items: z.array(wirePerson),
  policy_summary: z
    .object({
      is_default: bool(false),
      my_level: str,
      can_video: bool(true),
      can_group: bool(true),
      group_max: z.number().nullish(),
      ring_seconds: z.number().nullish(),
      /* v2: not in the addendum's list — read when the platform sends it */
      can_override_dnd: z.boolean().nullish(),
    })
    .nullish(),
  contacts: z
    .array(
      z.object({
        person_id: z.union([z.string(), z.number()]).transform(String),
        name: z.string(),
        org: str,
        role: str,
        phones: z.array(wirePhone).nullish(),
      }),
    )
    .nullish(),
});

const participantState = z.enum(["ringing", "joined", "declined", "missed", "left", "dropped", "waiting", "removed"]);
const role = z
  .string()
  .nullish()
  .transform((r) => (r === "host" || r === "cohost" || r === "member" ? r : undefined));

const wireSubject = z.object({
  type: z.enum(["principal", "role", "level", "anyone"]),
  id: z.union([z.string(), z.number()]).nullish(),
});

const wireJoin = z.object({
  mode: z
    .string()
    .nullish()
    .transform((m) => (m === "approval" || m === "open" ? m : "invited")),
  allow: z.array(wireSubject).nullish(),
});

const wireParticipant = z.object({
  principal_id: z.string(),
  name: z.string().nullish(),
  level: str,
  state: participantState,
  role,
  hand_raised: bool(false),
  hand_at: str,
  sharing: bool(false),
  audio_on: bool(true),
  video_on: bool(false),
  has_photo: bool(false),
  employee_id: str,
  invited_by: str,
  joined_at: str,
});

export const wireCall = z.object({
  id: z.string(),
  kind: z.enum(["direct", "group"]),
  media: z.enum(["audio", "video"]),
  title: str,
  state: z.enum(["ringing", "active", "ended"]),
  started_by: z.string(),
  started_by_name: str,
  created_at: z.string(),
  answered_at: str,
  ended_at: str,
  end_reason: z.enum(["completed", "missed", "declined", "cancelled", "dropped"]).nullish(),
  me: z
    .object({
      state: participantState,
      audio_on: bool(true),
      video_on: bool(false),
      principal_id: str,
      role,
      hand_raised: bool(false),
      sharing: bool(false),
    })
    .nullish(),
  participants: z.array(wireParticipant),
  /* v2 */
  host: str,
  join: wireJoin.nullish(),
  locked: bool(false),
  urgent: bool(false),
  meeting_id: str,
});

export const wireLiveCall = wireCall.extend({
  can: z
    .string()
    .nullish()
    .transform((c) => (c === "ask" ? ("ask" as const) : ("join" as const))),
});

export const wireDnd = z.object({
  on: bool(false),
  until: str,
  forever: bool(false),
  allow: z.array(z.object({ principal_id: z.string(), name: z.string().nullish() })).nullish(),
});

export const wireMessage = z.object({
  id: z.number(),
  call_id: z.string(),
  from: z.string(),
  name: z.string().nullish(),
  text: z.string(),
  created_at: z.string().nullish(),
});

export const wireMeeting = z.object({
  id: z.string(),
  title: z.string(),
  agenda: str,
  starts_at: z.string(),
  ends_at: str,
  duration_min: z.number().nullish(),
  organiser: z.string(),
  organiser_name: str,
  invitees: z
    .array(
      z.object({
        principal_id: z.string(),
        name: z.string().nullish(),
        level: str,
        has_photo: bool(false),
        employee_id: str,
      }),
    )
    .nullish(),
  join: wireJoin.nullish(),
  media: z.enum(["audio", "video"]).nullish(),
  state: z
    .string()
    .nullish()
    .transform((x) => (x === "live" || x === "ended" || x === "cancelled" ? x : "scheduled")),
  call_id: str,
  can_start: bool(false),
  can_edit: bool(false),
});

const wireSignal = z.object({
  id: z.number(),
  call_id: z.string(),
  from: z.string(),
  kind: z.enum(["offer", "answer", "ice", "hangup", "control"]),
  payload: z.unknown(),
  created_at: z.string().nullish(),
});

export const wirePoll = z.object({
  now: z.string().nullish(),
  incoming: z.array(wireCall).nullish(),
  calls: z.array(wireCall).nullish(),
  signals: z.array(wireSignal).nullish(),
  last_signal: z.number().nullish(),
  /* not in the contract yet: the caller's own principal, read when present */
  me: z.union([z.string(), z.object({ principal_id: z.string() })]).nullish(),
  /* v2 */
  dnd: wireDnd.nullish(),
  messages: z.array(wireMessage).nullish(),
  last_message: z.number().nullish(),
  meetings: z.array(wireMeeting).nullish(),
  waiting: z.array(wireCall).nullish(),
});

export const wireIce = z.object({
  ice_servers: z
    .array(
      z.object({
        urls: z.union([z.string(), z.array(z.string())]).transform((u) => (Array.isArray(u) ? u : [u])),
        username: str,
        credential: str,
      }),
    )
    .nullish(),
  relay: bool(false),
});

const levelMap = z.record(z.string(), z.boolean());

export const wirePolicy = z.object({
  matrix: z.record(z.string(), levelMap),
  video: levelMap,
  group_start: levelMap,
  group_max: z.number(),
  ring_seconds: z.number(),
  dnd_override: levelMap.nullish(),
  exceptions: z
    .array(
      z.object({
        id: z.string(),
        from: z.object({ type: z.enum(["principal", "role", "level", "anyone"]), id: z.string().nullish() }),
        to: z.object({ type: z.enum(["principal", "role", "level", "anyone"]), id: z.string().nullish() }),
        effect: z.enum(["allow", "deny"]),
        note: z.string().nullish(),
      }),
    )
    .nullish(),
});

export const wirePolicyView = z.object({
  policy: wirePolicy,
  is_default: bool(false),
  levels: z.array(z.string()).nullish(),
  roles: z.array(z.object({ key: z.string(), name: z.string().nullish(), level: str })).nullish(),
  /* build 5: absent from a platform older than 0091 */
  updated_at: z.string().nullish(),
  updated_by: z.string().nullish(),
});

export const wireCheck = z.object({
  allowed: z.boolean(),
  video: bool(false),
  reason: z.string().nullish(),
  rule: z.enum(["matrix", "exception", "self", "disabled", "no_calls"]).nullish(),
});

/* ── into the web app's names ─────────────────────────────────────────── */

const phones = (p: z.infer<typeof wirePhone>[] | null | undefined) =>
  (p ?? []).map((x) => ({ label: x.label ?? x.kind ?? "", value: x.value, kind: x.kind ?? undefined }));

export function fromWireDirectory(raw: z.infer<typeof wireDirectory>): CallDirectory {
  const ps = raw.policy_summary;
  return {
    summary: ps
      ? {
          isDefault: ps.is_default,
          myLevel: level(ps.my_level),
          canVideo: ps.can_video,
          canGroup: ps.can_group,
          groupMax: ps.group_max ?? 6,
          ringSeconds: ps.ring_seconds ?? 45,
          canOverrideDnd: ps.can_override_dnd ?? undefined,
        }
      : undefined,
    items: raw.items.map((p): CallPerson => ({
      principalId: p.principal_id,
      name: p.name,
      level: level(p.level),
      roles: p.roles ?? [],
      jobTitle: p.job_title ?? undefined,
      department: p.department ?? undefined,
      employeeId: p.employee_id ?? undefined,
      hasPhoto: p.has_photo,
      online: p.online,
      device: p.device === "web" || p.device === "phone" ? p.device : undefined,
      lastSeenAt: p.last_seen_at ?? undefined,
      canCall: p.can_call,
      canVideo: p.can_video,
      reason: p.reason ?? undefined,
      phones: phones(p.phones),
      dnd: p.dnd,
      dndUntil: p.dnd && p.dnd_until && !p.dnd_until.startsWith("infinity") ? p.dnd_until : undefined,
    })),
    contacts: (raw.contacts ?? []).map((c) => ({
      personId: c.person_id,
      name: c.name,
      org: c.org ?? undefined,
      role: c.role ?? undefined,
      phones: phones(c.phones),
    })),
  };
}

function fromWireParticipant(p: z.infer<typeof wireParticipant>): Participant {
  return {
    principalId: p.principal_id,
    name: p.name ?? "",
    level: p.level ? level(p.level) : undefined,
    state: p.state,
    audioOn: p.audio_on,
    videoOn: p.video_on,
    hasPhoto: p.has_photo,
    employeeId: p.employee_id ?? undefined,
    invitedBy: p.invited_by ?? undefined,
    joinedAt: p.joined_at ?? undefined,
    role: p.role,
    handRaised: p.hand_raised,
    handAt: p.hand_at ?? undefined,
    sharing: p.sharing,
  };
}

export function fromWireJoin(j: z.infer<typeof wireJoin> | null | undefined): JoinRule | undefined {
  if (!j) return undefined;
  return {
    mode: j.mode,
    allow: (j.allow ?? []).map((a): PolicyEnd => ({ type: a.type, id: a.id == null ? null : String(a.id) })),
  };
}

export function fromWireCall(c: z.infer<typeof wireCall>): Call {
  return {
    id: c.id,
    kind: c.kind,
    media: c.media,
    title: c.title ?? undefined,
    state: c.state,
    startedBy: c.started_by,
    startedByName: c.started_by_name ?? undefined,
    createdAt: c.created_at,
    answeredAt: c.answered_at ?? undefined,
    endedAt: c.ended_at ?? undefined,
    endReason: c.end_reason ?? undefined,
    me: {
      state: c.me?.state ?? "left",
      audioOn: c.me?.audio_on ?? true,
      videoOn: c.me?.video_on ?? false,
      principalId: c.me?.principal_id ?? undefined,
      role: c.me?.role,
      handRaised: c.me?.hand_raised ?? false,
      sharing: c.me?.sharing ?? false,
    },
    participants: c.participants.map(fromWireParticipant),
    host: c.host ?? undefined,
    join: fromWireJoin(c.join),
    locked: c.locked,
    urgent: c.urgent,
    meetingId: c.meeting_id ?? undefined,
  };
}

export function fromWireLiveCall(c: z.infer<typeof wireLiveCall>): LiveCall {
  return { ...fromWireCall(c), can: c.can };
}

export function fromWireDnd(d: z.infer<typeof wireDnd>): DndState {
  const forever = d.forever || (d.until ?? "").startsWith("infinity");
  return {
    on: d.on,
    until: forever ? undefined : (d.until ?? undefined),
    forever,
    allow: (d.allow ?? []).map((a) => ({ principalId: a.principal_id, name: a.name ?? "" })),
  };
}

export function fromWireMessage(m: z.infer<typeof wireMessage>): CallMessage {
  return {
    id: m.id,
    callId: m.call_id,
    from: m.from,
    name: m.name ?? "",
    text: m.text,
    createdAt: m.created_at ?? "",
  };
}

export function fromWireMeeting(m: z.infer<typeof wireMeeting>): Meeting {
  const duration = m.duration_min ?? 30;
  return {
    id: m.id,
    title: m.title,
    agenda: m.agenda ?? undefined,
    startsAt: m.starts_at,
    endsAt: m.ends_at ?? new Date(Date.parse(m.starts_at) + duration * 60000).toISOString(),
    durationMin: duration,
    organiser: m.organiser,
    organiserName: m.organiser_name ?? undefined,
    invitees: (m.invitees ?? []).map((i) => ({
      principalId: i.principal_id,
      name: i.name ?? "",
      level: i.level ? level(i.level) : undefined,
      hasPhoto: i.has_photo,
      employeeId: i.employee_id ?? undefined,
    })),
    join: fromWireJoin(m.join) ?? { mode: "invited", allow: [] },
    media: m.media ?? "video",
    state: m.state,
    callId: m.call_id ?? undefined,
    canStart: m.can_start,
    canEdit: m.can_edit,
  };
}

export function toWireJoin(j: JoinRule) {
  return { mode: j.mode, allow: j.allow.map((a) => ({ type: a.type, id: a.id })) };
}

function fromWireSignal(s: z.infer<typeof wireSignal>): Signal {
  return { id: s.id, callId: s.call_id, from: s.from, kind: s.kind, payload: s.payload, createdAt: s.created_at ?? "" };
}

export function fromWirePoll(raw: z.infer<typeof wirePoll>): CallPoll {
  const signals = (raw.signals ?? []).map(fromWireSignal);
  return {
    now: raw.now ?? new Date().toISOString(),
    incoming: (raw.incoming ?? []).map(fromWireCall),
    calls: (raw.calls ?? []).map(fromWireCall),
    signals,
    lastSignal: raw.last_signal ?? signals.reduce((m, s) => Math.max(m, s.id), 0),
    me: typeof raw.me === "string" ? raw.me : (raw.me?.principal_id ?? undefined),
    dnd: raw.dnd ? fromWireDnd(raw.dnd) : undefined,
    messages: (raw.messages ?? []).map(fromWireMessage),
    lastMessage: raw.last_message ?? (raw.messages ?? []).reduce((m, x) => Math.max(m, x.id), 0),
    meetings: (raw.meetings ?? []).map(fromWireMeeting),
    waiting: raw.waiting ? raw.waiting.map(fromWireCall) : undefined,
  };
}

export function fromWireIce(raw: z.infer<typeof wireIce>): IceConfig {
  return {
    iceServers: (raw.ice_servers ?? []).map((s) => ({
      urls: s.urls,
      ...(s.username ? { username: s.username } : {}),
      ...(s.credential ? { credential: s.credential } : {}),
    })),
    relay: raw.relay,
  };
}

const levels = (m: Record<string, boolean>, fallback: Record<Level, boolean>): Record<Level, boolean> =>
  Object.fromEntries(LEVELS.map((l) => [l, m[l] ?? fallback[l]])) as Record<Level, boolean>;

export function fromWirePolicy(p: z.infer<typeof wirePolicy>): CallPolicy {
  const d = defaultPolicy();
  return {
    matrix: Object.fromEntries(LEVELS.map((a) => [a, levels(p.matrix[a] ?? {}, d.matrix[a])])) as CallPolicy["matrix"],
    video: levels(p.video, d.video),
    groupStart: levels(p.group_start, d.groupStart),
    groupMax: p.group_max,
    ringSeconds: p.ring_seconds,
    dndOverride: levels(p.dnd_override ?? {}, d.dndOverride),
    exceptions: (p.exceptions ?? []).map((x) => ({
      id: x.id,
      from: { type: x.from.type, id: x.from.id ?? null },
      to: { type: x.to.type, id: x.to.id ?? null },
      effect: x.effect,
      note: x.note ?? "",
    })),
  };
}

export function toWirePolicy(p: CallPolicy) {
  return {
    matrix: p.matrix,
    video: p.video,
    group_start: p.groupStart,
    group_max: p.groupMax,
    ring_seconds: p.ringSeconds,
    dnd_override: p.dndOverride,
    exceptions: p.exceptions.map((x) => ({ id: x.id, from: x.from, to: x.to, effect: x.effect, note: x.note })),
  };
}

export function fromWirePolicyView(raw: z.infer<typeof wirePolicyView>): PolicyView {
  return {
    policy: fromWirePolicy(raw.policy),
    isDefault: raw.is_default,
    levels: (raw.levels ?? LEVELS).map((l) => level(l)),
    roles: (raw.roles ?? []).map((r) => ({ key: r.key, name: r.name ?? r.key, level: level(r.level) })),
    updatedAt: raw.updated_at ?? undefined,
    updatedBy: raw.updated_by ?? undefined,
  };
}
